import React from 'react';
import { TranslateFunc } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { makeStyles } from 'tss-react/mui';
import { UserEntity } from 'ha/types/UserEntity';
import { Tooltip, units, Grid, Link } from '@hbf/dsl';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import {
  CreditCard1AlternateBold,
  InformationCircleBold,
  PhoneLight,
  EnvelopeLight,
  SocialMediaFacebook1Light,
} from '@ha/icons';
import IdCardIcon from 'ha/custom-icons/id-card.svg';

const useStyles = makeStyles()(() => ({
  root: {
    paddingBottom: units.rem(1),
  },
  content: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    padding: units.yx(0.5, 1),
    borderLeft: '3px solid #00af9e !important',
    background: '#f8f9fa',
    color: '#768a99',
    lineHeight: 1.8,
    fontSize: units.rem(0.85),
    fontWeight: 'normal',
  },
  verification: {
    marginBottom: units.rem(0.25),
  },
  icon: {
    fontSize: units.rem(0.75),
    marginLeft: units.rem(0.5),
    verticalAlign: 'middle',
  },
  iconType: {
    fontSize: units.rem(1),
    marginRight: units.rem(0.5),
    position: 'relative',
    top: units.rem(0.25),
  },
  tooltip: {
    alignSelf: 'end',
  },
}));

const renderHintPopover = (T: TranslateFunc, url: string) => (
  <div>
    {T(
      'For your own privacy, you can’t call/email advertisers before your booking is confirmed. Use our secure real-time messaging to communicate with advertisers on the platform.',
    )}{' '}
    <Track name="Clicked Legal rental information in user card" type="link">
      <Link to={url} kind="mint-underline" target="_blank">
        {T('Read more here.')}
      </Link>
    </Track>
  </div>
);

interface Props {
  user: UserEntity;
}

const UserCardVerifications: React.FC<Props> = ({ user }) => {
  const { T, urlResolver } = useIntl();
  const { classes } = useStyles();

  return (
    <section className={classes.root}>
      <div className={classes.content}>
        <div>
          {user.isKYCDone && (
            <div
              data-test-locator="Identity Verified"
              className={classes.verification}
            >
              <span>
                <IdCardIcon className={classes.iconType} />
              </span>
              <strong>{T('identity')}</strong>
              <span>{` ${T('verified')}`}</span>
            </div>
          )}
          {user.hasPhone && (
            <div
              data-test-locator="User Card Phone Verified"
              className={classes.verification}
            >
              <span>
                <PhoneLight className={classes.iconType} />
              </span>
              <strong>{T('Phone number')}</strong>
              <span>{` ${T('verified')}`}</span>
            </div>
          )}
          {user.isEmailVerified && (
            <Grid
              container
              justifyContent="space-between"
              className={classes.verification}
              data-test-locator="User Card Email Verified"
            >
              <div>
                <span>
                  <EnvelopeLight className={classes.iconType} />
                </span>
                <strong>{T('Email')}</strong>
                <span>{` ${T('verified')}`}</span>
              </div>
              <div className={classes.tooltip}>
                <Tooltip
                  title={renderHintPopover(T, urlResolver.getDirectMessaging())}
                  placement="top"
                  leaveDelay={2000}
                >
                  <span>
                    <InformationCircleBold
                      className={classes.icon}
                      color="disabled"
                    />
                  </span>
                </Tooltip>
              </div>
            </Grid>
          )}
          {user.hasFacebookProfile && (
            <div
              data-test-locator="User Card Facebook Verified"
              className={classes.verification}
            >
              <span>
                <SocialMediaFacebook1Light className={classes.iconType} />
              </span>
              <strong>{T('Facebook')}</strong>
              <span>{` ${T('connected')}`}</span>
            </div>
          )}
          {user.hasPaymentMethod && (
            <div
              data-test-locator="User Card Has Paymentmethod"
              className={classes.verification}
            >
              <span>
                <CreditCard1AlternateBold className={classes.iconType} />
              </span>
              <strong>{T('Payment method')}</strong>
              <span>{` ${T('verified')}`}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export { UserCardVerifications };
